/**
 * LeadPartnerField — поле «Партнер» в карточке лида с явным UX:
 *
 *  - Партнёр НЕ привязан + canEdit: кнопка «+ Создать партнёра» (открывает
 *    стандартную форму CrmExtensions.Partners.createForm) и встроенный
 *    PartnerPicker с placeholder «Выбрать существующего…». Привязка
 *    выполняется через onChange(newPartnerId); серверный hook
 *    `LeadRepository.onAfterUpdate` синхронизирует контактные лица и каналы
 *    лида с новым партнёром (см. lead-events.ts).
 *
 *  - Партнёр привязан: имя кликабельно (drawer карточки), справа — иконка ×.
 *    Иконка появляется только при ховере на строку и подсвечена красным —
 *    деструктивную операцию визуально не «приглашаем» нажать. Клик ×
 *    показывает confirm-диалог (danger=true) с объяснением последствий.
 *
 *  - canEdit=false: только имя как Anchor (или dimmed «Не указан»).
 *
 * Открепление сейчас не имеет временного ограничения, но в перспективе должно
 * быть запрещено через ~1 день после привязки — операция считается крайне
 * нежелательной (контакты уже могли переехать к партнёру, история лида
 * связалась с ним, и т.д.).
 */
import { ActionIcon, Anchor, Button, Group, Text, Tooltip } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { IconPlus, IconX } from '@tabler/icons-react';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useConfirm } from '@ayocore/platform/client/hooks/useConfirm';
import { CrmExtensions } from '@ayocore/erp/crm/client/extensions';
import { PartnerPicker } from '@ayocore/erp/crm/client/components/Partners';

export interface LeadPartnerFieldProps {
  /** Текущий объект партнёра, прокидывается родителем (lead.partner). */
  partner?: {
    id: number;
    name?: string | null;
    full_name?: string | null;
    inn?: string | null;
  } | null;
  /** Колбек привязки / отвязки партнёра. null = открепить. */
  onChange: (partnerId: number | null) => void;
  /** Колбек клика по имени партнёра — для открытия карточки. */
  onPartnerClick?: (partnerId: number) => void;
  /** Если false — поле read-only. */
  canEdit?: boolean;
  /**
   * Лид, в карточке которого находимся. Уходит в `created_from_lead_id`
   * создаваемого партнёра — пометка происхождения, не источник.
   */
  leadId?: number;
  /** Источник лида — подставляется новому партнёру как его собственный. */
  leadSourceId?: number | null;
}

export function LeadPartnerField({
  partner,
  onChange,
  onPartnerClick,
  canEdit = false,
  leadId,
  leadSourceId,
}: LeadPartnerFieldProps) {
  const createPartnerModal = useModal(CrmExtensions.Partners.createForm.get());

  const partnerName = partner?.name ?? partner?.full_name ?? null;

  // === Read-only ===
  if (!canEdit) {
    if (!partner || !partnerName) {
      return (
        <Text size='sm' c='dimmed' fs='italic'>
          Не указан
        </Text>
      );
    }
    return (
      <Anchor
        size='sm'
        onClick={() => onPartnerClick?.(partner.id)}
        style={{ color: '#6e4be2' }}
      >
        {partnerName}
      </Anchor>
    );
  }

  // === Привязан + editable ===
  if (partner && partnerName) {
    return (
      <AttachedPartnerRow
        partner={partner}
        partnerName={partnerName}
        onPartnerClick={onPartnerClick}
        onChange={onChange}
      />
    );
  }

  // === Не привязан + editable: кнопка создать + inline-пикер выбора ===
  const handleCreateClick = () => {
    createPartnerModal.open(
      {
        title: 'Создать партнёра',
        // Партнёр заводится из этого лида: помечаем происхождение и
        // переносим источник лида в собственный источник партнёра.
        createdFromLeadId: leadId ?? null,
        defaultSourceId: leadSourceId ?? null,
        onPartnerCreated: (newPartnerId?: number) => {
          if (typeof newPartnerId === 'number') {
            onChange(newPartnerId);
          }
          createPartnerModal.close();
        },
      },
      { size: 'lg' }
    );
  };

  return (
    <Group gap={8} wrap='nowrap'>
      <Button
        size='xs'
        variant='light'
        leftSection={<IconPlus size={14} />}
        onClick={handleCreateClick}
      >
        Создать партнёра
      </Button>
      <PartnerPicker
        value={null}
        partner={null}
        onChange={id => {
          if (id) onChange(id);
        }}
        placeholder='Выбрать существующего…'
      />
    </Group>
  );
}

/**
 * Привязанный партнёр в edit-режиме. Кнопка × появляется только при ховере на строку
 * и подсвечена красным — деструктивную операцию визуально не «приглашаем» нажать.
 * Слот под иконку зарезервирован (opacity-toggle), чтобы layout не дёргался.
 */
function AttachedPartnerRow({
  partner,
  partnerName,
  onPartnerClick,
  onChange,
}: {
  partner: NonNullable<LeadPartnerFieldProps['partner']>;
  partnerName: string;
  onPartnerClick: LeadPartnerFieldProps['onPartnerClick'];
  onChange: LeadPartnerFieldProps['onChange'];
}) {
  const confirm = useConfirm();
  const { hovered, ref } = useHover<HTMLDivElement>();

  const handleDetach = async () => {
    const ok = await confirm(
      'Открепить партнёра от лида? Связь между лидом и партнёром будет удалена. ' +
        'Контактные лица и контактные каналы, которые ранее были перенесены к партнёру, ' +
        'у него останутся — лид их потеряет. Операция нежелательна: используйте её ' +
        'только если партнёр был выбран по ошибке.',
      {
        title: 'Открепить партнёра',
        confirmLabel: 'Открепить',
        cancelLabel: 'Отмена',
        danger: true,
      }
    );
    if (!ok) return;
    onChange(null);
  };

  return (
    <Group ref={ref} gap={6} wrap='nowrap' style={{ minWidth: 0 }}>
      <Anchor
        size='sm'
        onClick={() => onPartnerClick?.(partner.id)}
        style={{
          color: '#6e4be2',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        {partnerName}
      </Anchor>
      <Tooltip label='Открепить партнёра' withArrow>
        <ActionIcon
          size='sm'
          variant='subtle'
          color='red'
          onClick={handleDetach}
          aria-label='Открепить партнёра'
          style={{
            opacity: hovered ? 1 : 0,
            pointerEvents: hovered ? 'auto' : 'none',
            transition: 'opacity 0.12s ease',
          }}
        >
          <IconX size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
